/**
 * @fileoverview Dev tool for inspecting React elements - copies styling/debugging context to clipboard
 * @module shared/components/StagewiseToolbarWrapper
 *
 * Usage:
 * - Alt+Click to select/deselect elements
 * - Escape to copy selected elements to clipboard
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { ReactElement, useEffect } from 'react'

/** Theme token definition with source location */
interface ThemeToken {
  name: string
  definition: string
  source: string
  computed?: string | undefined
}

/** Extracted element data optimized for styling and debugging */
interface CleanElementData {
  source: string | null
  component: string | null
  tag: string
  classes: string
  id: string | null
  style: string | null
  dimensions: string
  xpath: string
  text: string
  parents: Array<{ tag: string; classes: string }>
  children: Array<{ tag: string; classes: string }>
  tokens: ThemeToken[]
}

/** Parsed token registry entry */
interface TokenRegistryEntry {
  definition: string
  source: string
}

/** Dynamic token registry - populated by parsing CSS files */
const tokenRegistry: Record<string, TokenRegistryEntry> = {}
let registryPromise: Promise<void> | null = null

/**
 * Fetches and parses CSS files to build the token registry
 * Extracts all CSS variable definitions with their line numbers
 * Returns a promise that resolves when loading is complete
 */
function loadTokenRegistry(): Promise<void> {
  if (registryPromise) return registryPromise

  registryPromise = (async () => {
    // Parse CSS content and extract variable definitions
    const parseCssContent = (cssText: string, fileName: string) => {
      const lines = cssText.split('\n')
      const varRegex = /^\s*(--[\w-]+)\s*:\s*(.+?)\s*;?\s*$/

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        if (!line) continue
        const match = line.match(varRegex)
        if (match && match[1] && match[2]) {
          const varName = match[1]
          const value = match[2]
          if (!tokenRegistry[varName]) {
            tokenRegistry[varName] = {
              definition: value.replace(/;$/, '').trim(),
              source: `${fileName}:${i + 1}`,
            }
          }
        }
      }
    }

    try {
      // Use Vite's ?raw import to get raw CSS content
      const indexCss = await import('/src/index.css?raw')
      if (indexCss.default) {
        parseCssContent(indexCss.default, 'index.css')
      }
    } catch {
      // Silently fail - tokens just won't be available
    }

    try {
      const themeTokensCss = await import('/src/styles/theme-tokens.css?raw')
      if (themeTokensCss.default) {
        parseCssContent(themeTokensCss.default, 'theme-tokens.css')
      }
    } catch {
      // Silently fail
    }
  })()

  return registryPromise
}

/**
 * Infers CSS variable name from a Tailwind class
 * e.g., "bg-card" -> "--card", "text-muted-foreground" -> "--muted-foreground"
 */
function inferTokenFromClass(cls: string): string | null {
  // Remove opacity modifier (e.g., "border-border/15" -> "border-border")
  const baseClass = cls.split('/')[0] ?? cls

  // Tailwind utility prefixes that map to CSS variables
  const prefixPatterns: Array<{ prefix: string; extract: (s: string) => string | null }> = [
    // Background: bg-{token} -> --{token}
    {
      prefix: 'bg-',
      extract: s => {
        const token = s.slice(3)
        // Skip standard Tailwind colors/utilities
        if (/^(transparent|current|inherit|white|black|slate|gray|zinc|neutral|stone|red|orange|amber|yellow|lime|green|emerald|teal|cyan|sky|blue|indigo|violet|purple|fuchsia|pink|rose)/.test(token)) {
          return null
        }
        if (/^(fixed|local|scroll|clip|gradient|none|center|bottom|top|left|right)/.test(token)) {
          return null
        }
        return `--${token}`
      },
    },
    // Text: text-{token} -> --{token}
    {
      prefix: 'text-',
      extract: s => {
        const token = s.slice(5)
        // Skip standard Tailwind text utilities
        if (/^(xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl|7xl|8xl|9xl)$/.test(token)) {
          return null
        }
        if (/^(left|center|right|justify|start|end|wrap|nowrap|ellipsis|clip)$/.test(token)) {
          return null
        }
        if (/^(transparent|current|inherit|white|black|slate|gray|zinc|neutral|stone|red|orange|amber|yellow|lime|green|emerald|teal|cyan|sky|blue|indigo|violet|purple|fuchsia|pink|rose)/.test(token)) {
          return null
        }
        return `--${token}`
      },
    },
    // Border: border-{token} -> --{token}
    {
      prefix: 'border-',
      extract: s => {
        const token = s.slice(7)
        // Skip standard Tailwind border utilities
        if (/^(0|2|4|8|solid|dashed|dotted|double|hidden|none|collapse|separate)$/.test(token)) {
          return null
        }
        if (/^(t|r|b|l|x|y|s|e)-/.test(token)) {
          return null
        }
        if (/^(transparent|current|inherit|white|black|slate|gray|zinc|neutral|stone|red|orange|amber|yellow|lime|green|emerald|teal|cyan|sky|blue|indigo|violet|purple|fuchsia|pink|rose)/.test(token)) {
          return null
        }
        return `--${token}`
      },
    },
    // Ring: ring-{token} -> --{token}
    {
      prefix: 'ring-',
      extract: s => {
        const token = s.slice(5)
        if (/^(0|1|2|4|8|inset|offset)/.test(token)) {
          return null
        }
        if (/^(transparent|current|inherit|white|black|slate|gray|zinc|neutral|stone|red|orange|amber|yellow|lime|green|emerald|teal|cyan|sky|blue|indigo|violet|purple|fuchsia|pink|rose)/.test(token)) {
          return null
        }
        return `--${token}`
      },
    },
    // Shadow: shadow-{token} -> --shadow-{token}
    {
      prefix: 'shadow-',
      extract: s => {
        const token = s.slice(7)
        if (/^(sm|md|lg|xl|2xl|inner|none)$/.test(token)) {
          return null
        }
        return `--shadow-${token}`
      },
    },
  ]

  for (const { prefix, extract } of prefixPatterns) {
    if (baseClass.startsWith(prefix)) {
      return extract(baseClass)
    }
  }

  return null
}

const StagewiseToolbarWrapper = (): ReactElement | null => {
  useEffect(() => {
    if (!import.meta.env.DEV) {
      return
    }

    // Load token registry from CSS files (async, non-blocking)
    loadTokenRegistry()

    let altKeyPressed = false
    let currentHighlight: HTMLElement | null = null
    let selectedElements: HTMLElement[] = []
    let selectedHighlights: HTMLElement[] = []

    // ============ Theme Token Extraction ============

    /**
     * Extracts theme tokens from element classes using dynamic inference
     * Returns tokens with their definitions, source locations, and computed values
     */
    const extractThemeTokens = (_el: HTMLElement, classes: string): ThemeToken[] => {
      const tokens: ThemeToken[] = []
      const seenTokens = new Set<string>()
      const classList = classes.split(/\s+/).filter(Boolean)

      for (const cls of classList) {
        // Infer token name from class
        const tokenName = inferTokenFromClass(cls)
        if (!tokenName || seenTokens.has(tokenName)) continue

        // Check if this token exists in the registry
        const registry = tokenRegistry[tokenName]
        if (registry) {
          seenTokens.add(tokenName)

          // Get computed value from the root element
          const computedStyle = getComputedStyle(document.documentElement)
          const computed = computedStyle.getPropertyValue(tokenName).trim()

          tokens.push({
            name: tokenName,
            definition: registry.definition,
            source: registry.source,
            computed: computed || undefined,
          })
        }
      }

      return tokens
    }

    // ============ Source Location Extraction ============

    /**
     * Extracts source file location from React fiber's _debugStack
     * Returns format like "ComponentName.tsx:42"
     */
    const extractSourceLocation = (el: HTMLElement): string | null => {
      try {
        const fiberKey = Object.getOwnPropertyNames(el).find(
          key => key.startsWith('__reactFiber$') || key.startsWith('__reactInternalInstance$')
        )
        if (!fiberKey) return null

        const fiber = (el as any)[fiberKey]
        if (!fiber?._debugStack?.stack) return null

        const stack: string = fiber._debugStack.stack
        // Look for source file in stack trace - match pattern like "ComponentName.tsx:42:10"
        const match = stack.match(/at \w+ \(.*?\/([^/]+\.tsx?):(\d+):\d+\)/)
        if (match) {
          return `${match[1]}:${match[2]}`
        }

        // Try alternative pattern for anonymous functions
        const altMatch = stack.match(/\/([^/]+\.tsx?):(\d+):\d+/)
        if (altMatch) {
          return `${altMatch[1]}:${altMatch[2]}`
        }

        return null
      } catch {
        return null
      }
    }

    /**
     * Extracts React component name from fiber
     */
    const extractComponentName = (el: HTMLElement): string | null => {
      try {
        const fiberKey = Object.getOwnPropertyNames(el).find(
          key => key.startsWith('__reactFiber$') || key.startsWith('__reactInternalInstance$')
        )
        if (!fiberKey) return null

        let fiber = (el as any)[fiberKey]

        // Traverse up to find the first function/class component
        while (fiber) {
          // tag 0 = FunctionComponent, tag 1 = ClassComponent
          if (fiber.tag === 0 || fiber.tag === 1) {
            const type = fiber.type
            if (type) {
              return type.displayName || type.name || null
            }
          }
          fiber = fiber.return
        }

        return null
      } catch {
        return null
      }
    }

    // ============ Element Data Extraction ============

    const getXPath = (element: HTMLElement): string => {
      if (element.id) return `//*[@id="${element.id}"]`

      const parts: string[] = []
      let current: HTMLElement | null = element

      while (current && current.nodeType === Node.ELEMENT_NODE) {
        let index = 1
        let sibling = current.previousElementSibling

        while (sibling) {
          if (sibling.nodeName === current.nodeName) index++
          sibling = sibling.previousElementSibling
        }

        const tagName = current.nodeName.toLowerCase()
        const siblings = Array.from(current.parentElement?.children || []).filter(
          child => child.nodeName === current!.nodeName
        )

        if (siblings.length > 1) {
          parts.unshift(`${tagName}[${index}]`)
        } else {
          parts.unshift(tagName)
        }

        current = current.parentElement
      }

      return '/' + parts.join('/')
    }

    /**
     * Extract clean, focused element data for styling and debugging
     */
    const extractCleanElementData = (el: HTMLElement): CleanElementData => {
      const rect = el.getBoundingClientRect()

      // Get parent hierarchy (2 levels up)
      const parents: Array<{ tag: string; classes: string }> = []
      let parent = el.parentElement
      for (let i = 0; i < 2 && parent && parent !== document.body; i++) {
        parents.push({
          tag: parent.tagName.toLowerCase(),
          classes: parent.getAttribute('class') || '',
        })
        parent = parent.parentElement
      }

      // Get direct children (first 10)
      const children: Array<{ tag: string; classes: string }> = []
      const childElements = Array.from(el.children).slice(0, 10)
      for (const child of childElements) {
        if (child instanceof Element) {
          children.push({
            tag: child.tagName.toLowerCase(),
            classes: child.getAttribute('class') || '',
          })
        }
      }

      // Truncate text content
      const rawText = (el.textContent || '').trim()
      const text = rawText.length > 200 ? rawText.substring(0, 200) + '...' : rawText

      // Extract theme tokens from classes
      // Use getAttribute for SVG compatibility (SVG className is SVGAnimatedString, not string)
      const classes = el.getAttribute('class') || ''
      const tokens = extractThemeTokens(el, classes)

      return {
        source: extractSourceLocation(el),
        component: extractComponentName(el),
        tag: el.tagName.toLowerCase(),
        classes,
        id: el.id || null,
        style: el.getAttribute('style'),
        dimensions: `${Math.round(rect.width)} × ${Math.round(rect.height)}`,
        xpath: getXPath(el),
        text,
        parents,
        children,
        tokens,
      }
    }

    // ============ Clipboard Output Formatting ============

    const formatElementForClipboard = (data: CleanElementData, index: number): string => {
      const lines: string[] = []

      lines.push(`<element index="${index}">`)

      // Debugging info
      if (data.source) {
        lines.push(`  <source>${data.source}</source>`)
      }
      if (data.component) {
        lines.push(`  <component>${data.component}</component>`)
      }

      // Element identity
      lines.push(`  <tag>${data.tag}</tag>`)
      if (data.id) {
        lines.push(`  <id>${data.id}</id>`)
      }

      // Styling info
      lines.push(`  <classes>${data.classes}</classes>`)
      if (data.style) {
        lines.push(`  <style>${data.style}</style>`)
      }
      lines.push(`  <dimensions>${data.dimensions}</dimensions>`)

      // Context
      lines.push(`  <xpath>${data.xpath}</xpath>`)
      if (data.text) {
        lines.push(`  <text>${data.text}</text>`)
      }

      // Parent hierarchy
      if (data.parents.length > 0) {
        lines.push(`  <parents>`)
        data.parents.forEach((p, i) => {
          lines.push(`    <parent level="${i + 1}" tag="${p.tag}" classes="${p.classes}" />`)
        })
        lines.push(`  </parents>`)
      }

      // Children
      if (data.children.length > 0) {
        lines.push(`  <children count="${data.children.length}">`)
        data.children.forEach(c => {
          lines.push(`    <child tag="${c.tag}" classes="${c.classes}" />`)
        })
        lines.push(`  </children>`)
      }

      // Theme tokens
      if (data.tokens.length > 0) {
        lines.push(`  <tokens>`)
        data.tokens.forEach(t => {
          lines.push(`    <token name="${t.name}" source="${t.source}">`)
          lines.push(`      <definition>${t.definition}</definition>`)
          if (t.computed) {
            lines.push(`      <computed>${t.computed}</computed>`)
          }
          lines.push(`    </token>`)
        })
        lines.push(`  </tokens>`)
      }

      lines.push(`</element>`)

      return lines.join('\n')
    }

    const copyMultipleElementsToClipboard = async () => {
      if (selectedElements.length === 0) return

      try {
        // Ensure token registry is loaded before extracting
        await loadTokenRegistry()

        const elementsData = selectedElements.map(extractCleanElementData)

      const output = `<inspector>
  <url>${window.location.href}</url>
  <page>${document.title}</page>
  <selected count="${elementsData.length}">
${elementsData.map((data, i) => formatElementForClipboard(data, i)).join('\n\n')}
  </selected>
</inspector>`

      navigator.clipboard
        .writeText(output)
        .then(() => {
          const notification = document.createElement('div')
          notification.textContent = `Copied ${selectedElements.length} element(s) to clipboard`
          notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #10b981;
            color: white;
            padding: 12px 20px;
            border-radius: 8px;
            font-family: -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 14px;
            z-index: 10000;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          `

          document.body.appendChild(notification)
          setTimeout(() => notification.remove(), 2000)

          clearSelection()
        })
        .catch(err => {
          console.error('Failed to copy to clipboard:', err)
        })
      } catch (err) {
        console.error('Failed to extract element data:', err)
        const notification = document.createElement('div')
        notification.textContent = `Error: ${err instanceof Error ? err.message : 'Unknown error'}`
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: #ef4444;
          color: white;
          padding: 12px 20px;
          border-radius: 8px;
          font-family: -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 14px;
          z-index: 10000;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        `
        document.body.appendChild(notification)
        setTimeout(() => notification.remove(), 3000)
      }
    }

    // ============ Visual Feedback ============

    const createHighlightOverlay = (element: HTMLElement): HTMLElement => {
      const overlay = document.createElement('div')
      const rect = element.getBoundingClientRect()

      overlay.style.cssText = `
        position: fixed;
        top: ${rect.top - 2}px;
        left: ${rect.left - 2}px;
        width: ${rect.width + 4}px;
        height: ${rect.height + 4}px;
        border: 2px solid #22c55e;
        background: rgba(34, 197, 94, 0.1);
        pointer-events: none;
        z-index: 10000;
        border-radius: 4px;
        box-shadow: 0 0 8px rgba(34, 197, 94, 0.5);
        transition: all 0.1s ease;
      `

      const componentName = extractComponentName(element)
      const elementClasses = element.getAttribute('class') || ''
      const label = document.createElement('div')
      label.textContent = componentName
        ? `${element.tagName.toLowerCase()}.${componentName}`
        : `${element.tagName.toLowerCase()}${elementClasses ? '.' + elementClasses.split(' ')[0] : ''}`

      label.style.cssText = `
        position: absolute;
        top: -24px;
        left: 0;
        background: #22c55e;
        color: white;
        padding: 2px 8px;
        border-radius: 3px;
        font-size: 12px;
        font-family: monospace;
        white-space: nowrap;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      `

      overlay.appendChild(label)
      document.body.appendChild(overlay)

      return overlay
    }

    const removeHighlight = (): void => {
      if (currentHighlight) {
        currentHighlight.remove()
        currentHighlight = null
      }
    }

    const highlightElement = (element: HTMLElement): void => {
      removeHighlight()
      currentHighlight = createHighlightOverlay(element)
    }

    const createSelectedOverlay = (element: HTMLElement, index: number): HTMLElement => {
      const overlay = document.createElement('div')
      const rect = element.getBoundingClientRect()

      overlay.style.cssText = `
        position: fixed;
        top: ${rect.top - 2}px;
        left: ${rect.left - 2}px;
        width: ${rect.width + 4}px;
        height: ${rect.height + 4}px;
        border: 2px solid #3b82f6;
        background: rgba(59, 130, 246, 0.1);
        pointer-events: none;
        z-index: 9999;
        border-radius: 4px;
        box-shadow: 0 0 8px rgba(59, 130, 246, 0.5);
      `

      const badge = document.createElement('div')
      badge.textContent = `${index + 1}`
      badge.style.cssText = `
        position: absolute;
        top: -12px;
        right: -12px;
        background: #3b82f6;
        color: white;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: bold;
        font-family: monospace;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      `

      overlay.appendChild(badge)
      document.body.appendChild(overlay)

      return overlay
    }

    // ============ Selection Management ============

    const addToSelection = (element: HTMLElement) => {
      if (selectedElements.includes(element)) return

      selectedElements.push(element)
      const overlay = createSelectedOverlay(element, selectedElements.length - 1)
      selectedHighlights.push(overlay)

      updateSelectionMessage()
    }

    const removeFromSelection = (element: HTMLElement) => {
      const index = selectedElements.indexOf(element)
      if (index === -1) return

      selectedElements.splice(index, 1)
      const overlay = selectedHighlights.splice(index, 1)[0]
      if (overlay) {
        overlay.remove()
      }

      selectedHighlights.forEach((highlight, i) => {
        const badge = highlight.querySelector('div')
        if (badge) badge.textContent = `${i + 1}`
      })

      updateSelectionMessage()
    }

    const clearSelection = () => {
      selectedElements = []
      selectedHighlights.forEach(overlay => overlay.remove())
      selectedHighlights = []
      hideSelectionMessage()
    }

    const isSelected = (element: HTMLElement): boolean => {
      return selectedElements.includes(element)
    }

    // ============ UI Messages ============

    let selectionMessage: HTMLElement | null = null
    let inspectMessage: HTMLElement | null = null

    const updateSelectionMessage = () => {
      hideSelectionMessage()
      if (selectedElements.length === 0) return

      selectionMessage = document.createElement('div')

      const container = document.createElement('div')
      container.style.cssText = 'display: flex; align-items: center; gap: 8px;'

      const text = document.createElement('span')
      text.textContent = `${selectedElements.length} element${selectedElements.length > 1 ? 's' : ''} selected`

      const copyButton = document.createElement('button')
      copyButton.textContent = 'Copy'
      copyButton.style.cssText =
        'background: #10b981; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 12px;'
      copyButton.addEventListener('click', e => {
        e.preventDefault()
        e.stopPropagation()
        copyMultipleElementsToClipboard()
      })

      const clearButton = document.createElement('button')
      clearButton.textContent = 'Clear'
      clearButton.style.cssText =
        'background: #ef4444; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 12px;'
      clearButton.addEventListener('click', e => {
        e.preventDefault()
        e.stopPropagation()
        clearSelection()
      })

      container.appendChild(text)
      container.appendChild(copyButton)
      container.appendChild(clearButton)

      selectionMessage.appendChild(container)
      selectionMessage.style.cssText = `
        position: fixed;
        top: 60px;
        left: 50%;
        transform: translateX(-50%);
        background: #1f2937;
        color: #ffffff;
        padding: 8px 16px;
        border-radius: 6px;
        font-family: -apple-system, BlinkMacSystemFont, sans-serif;
        font-size: 14px;
        z-index: 10002;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        border: 1px solid #374151;
        pointer-events: auto;
      `

      document.body.appendChild(selectionMessage)
    }

    const hideSelectionMessage = () => {
      if (selectionMessage) {
        selectionMessage.remove()
        selectionMessage = null
      }
    }

    const showInspectModeMessage = () => {
      if (inspectMessage) return

      inspectMessage = document.createElement('div')
      inspectMessage.textContent = 'Alt+Click to select • Escape to copy'
      inspectMessage.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #1f2937;
        color: #10b981;
        padding: 8px 16px;
        border-radius: 6px;
        font-family: -apple-system, BlinkMacSystemFont, sans-serif;
        font-size: 14px;
        z-index: 10001;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        border: 1px solid #10b981;
        pointer-events: none;
      `
      document.body.appendChild(inspectMessage)
    }

    const hideInspectModeMessage = () => {
      if (inspectMessage) {
        inspectMessage.remove()
        inspectMessage = null
      }
    }

    // ============ Event Handlers ============

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && !altKeyPressed) {
        altKeyPressed = true
        document.body.style.cursor = 'crosshair'
        showInspectModeMessage()
      }

      if (e.key === 'Escape') {
        if (selectedElements.length > 0) {
          copyMultipleElementsToClipboard()
          e.preventDefault()
        }
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.altKey && altKeyPressed) {
        altKeyPressed = false
        document.body.style.cursor = ''
        removeHighlight()
        hideInspectModeMessage()
      }
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (altKeyPressed && e.target instanceof HTMLElement) {
        highlightElement(e.target)
      }
    }

    const handleMouseDownCapture = (e: MouseEvent) => {
      if ((e.altKey || altKeyPressed) && e.target) {
        const element = e.target as HTMLElement

        if (isSelected(element)) {
          removeFromSelection(element)
        } else {
          addToSelection(element)
        }

        e.preventDefault()
        e.stopImmediatePropagation()
        return false
      }
    }

    // ============ Setup & Cleanup ============

    document.addEventListener('keydown', handleKeyDown, true)
    document.addEventListener('keyup', handleKeyUp, true)
    document.addEventListener('mousemove', handleMouseMove, true)
    document.addEventListener('mousedown', handleMouseDownCapture, true)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      document.removeEventListener('keyup', handleKeyUp, true)
      document.removeEventListener('mousemove', handleMouseMove, true)
      document.removeEventListener('mousedown', handleMouseDownCapture, true)

      removeHighlight()
      clearSelection()
      hideInspectModeMessage()
      hideSelectionMessage()
      document.body.style.cursor = ''
    }
  }, [])

  // Only active in development mode
  if (import.meta.env.DEV) {
    return null
  }

  return null
}

export default StagewiseToolbarWrapper
